import { Payment } from '../../domain/entities/Payment';
import { PaymentRepository } from '../../domain/repositories/PaymentRepository';

/**
 * Servicio para la gestión de pagos
 */
export class PaymentService {
  private paymentRepository: PaymentRepository;
  
  /**
   * Constructor del servicio de pagos
   * @param paymentRepository - Repositorio de pagos a utilizar
   */
  constructor(paymentRepository: PaymentRepository) {
    this.paymentRepository = paymentRepository;
  }
  
  /**
   * Crea un nuevo registro de pago
   * @param paymentData - Datos del pago a crear
   * @throws Error si hay un problema con la creación del pago
   * @returns El pago creado
   */
  async createPayment(paymentData: Omit<Payment, 'id'>): Promise<Payment> {
    try {
      return await this.paymentRepository.create(paymentData);
    } catch (error) {
      console.error('Error al crear el pago:', error);
      throw new Error('Error al procesar el pago. Por favor, inténtelo de nuevo.');
    }
  }
  
  /**
   * Obtiene un pago por su ID
   * @param id - ID del pago a buscar
   * @throws Error si hay un problema al obtener el pago
   * @returns El pago encontrado o null si no existe
   */
  async getPaymentById(id: string): Promise<Payment | null> {
    try {
      return await this.paymentRepository.getById(id);
    } catch (error) {
      console.error('Error al obtener el pago:', error);
      throw new Error('Error al obtener la información del pago. Por favor, inténtelo de nuevo.');
    }
  }
  
  /**
   * Actualiza el estado de un pago
   * @param id - ID del pago a actualizar
   * @param status - Nuevo estado del pago
   * @throws Error si hay un problema con la actualización
   * @returns El pago actualizado
   */
  async updatePaymentStatus(id: string, status: Payment['status']): Promise<Payment> {
    try {
      return await this.paymentRepository.updateStatus(id, status);
    } catch (error) {
      console.error('Error al actualizar el estado del pago:', error);
      throw new Error('Error al actualizar el estado del pago. Por favor, inténtelo de nuevo.');
    }
  }
  
  /**
   * Obtiene todos los pagos asociados a una factura
   * @param invoiceId - ID de la factura
   * @throws Error si hay un problema al obtener los pagos
   * @returns Lista de pagos asociados a la factura
   */
  async getPaymentsByInvoiceId(invoiceId: string): Promise<Payment[]> {
    try {
      return await this.paymentRepository.getByInvoiceId(invoiceId);
    } catch (error) {
      console.error('Error al obtener los pagos de la factura:', error);
      throw new Error('Error al obtener el historial de pagos. Por favor, inténtelo de nuevo.');
    }
  }

  /**
   * Registra un pago en el sistema
   * @param paymentData - Datos del pago a registrar
   * @returns Promesa que resuelve con el ID del pago registrado
   */
  async registerPayment(paymentData: {
    uuid?: string;
    valorPagado: number;
    identificacion: string;
    estado: string;
    fechaPago: string;
    medioPago: string;
    factura: string;
    codigoFideicomiso?: string;
    oficina?: string;
    prefijo?: string;
    numeroFactura?: string;
    nombre?: string;
    detalleFactura?: string;
    fechaVencimiento?: string;
    intereses?: number;
    fechaFactura?: string;
    valorTotal?: number;
    secuencial?: number;
    valorSaldo?: number;
    id?: number;
  }): Promise<{ id: number }> {
    try {
      console.log("datos del pago: ",JSON.stringify(paymentData));
      return await this.paymentRepository.registerPayment(paymentData);
    } catch (error) {
      console.error('Error al registrar el pago:', error);
      throw new Error('Error al registrar el pago en el sistema');
    }
  }
} 